/**
 * Pure utility functions for AdminApartmentsPage (Step 7.4 Phase 1a).
 * NO React, NO hooks, NO JSX.
 * 1 Cat X constant: MODAL_COST_FIXED_SET.
 */
import {
  isLandlordContractLeaseStarted,
} from "../../../utils/unitOccupancyStatus";
import { getUnitMonthlyRunningCosts } from "../../../utils/adminUnitRunningCosts";
import {
  formatLandlordSummaryLine as landlordSelectLabel,
  formatLandlordCompactLabel,
} from "../../../utils/landlordDisplayFormat";
import { todayBusinessDate } from "../../../utils/datetime";

export function landlordDisplayForId(landlords, landlordId) {
  if (landlordId == null || String(landlordId).trim() === "") return "—";
  const lid = String(landlordId);
  const l = (landlords || []).find((x) => String(x.id) === lid);
  if (!l) return "—";
  return landlordSelectLabel(l);
}

export function landlordCompactForId(landlords, landlordId) {
  if (landlordId == null || String(landlordId).trim() === "") return "—";
  const lid = String(landlordId);
  const l = (landlords || []).find((x) => String(x.id) === lid);
  if (!l) return "—";
  return formatLandlordCompactLabel(l);
}

export function propertyManagerSelectLabel(pm) {
  const n = String(pm.name || "").trim();
  if (n) return n;
  const e = String(pm.email || "").trim();
  if (e) return e;
  return pm.id;
}

export function parseMoneyChf(raw) {
  if (raw === "" || raw == null) return 0;
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export function parseOptionalMoneyChf(raw) {
  if (raw === "" || raw == null) return null;
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function dateOnlyOrNull(raw) {
  const s = String(raw || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  return s.slice(0, 10);
}

export function computeLeaseStatusKey(formData) {
  const leaseStart = dateOnlyOrNull(formData?.leaseStartDate);
  const notice = dateOnlyOrNull(formData?.noticeGivenDate);
  const returned = dateOnlyOrNull(formData?.returnedToLandlordDate);
  const today = getTodayDateString();
  if (returned && returned <= today) return "ended";
  if (notice) return "notice_given";
  if (leaseStart) return "active";
  return "";
}

export function leaseStatusLabel(key) {
  if (key === "active") return "Aktiv";
  if (key === "notice_given") return "Gekündigt";
  if (key === "ended") return "Beendet";
  return "—";
}

export function strOrNull(raw) {
  const t = String(raw ?? "").trim();
  return t === "" ? null : t;
}

/** Geocoding line for unit modal (aligned with Liegenschaften / AdminPropertiesPage). */
export function unitGeocodingStatusPresentation(meta, snapshot, isCreate) {
  if (isCreate) {
    return {
      text: "Koordinaten: Noch nicht berechnet",
      className: "text-[11px] text-[#64748b] dark:text-[#6b7a9a]",
    };
  }
  if (meta) {
    if (meta.status === "ok") {
      return {
        text: "Koordinaten: Erfolgreich",
        className: "text-[11px] font-medium text-emerald-600 dark:text-emerald-400",
      };
    }
    if (meta.status === "skipped") {
      if (meta.reason === "unchanged") {
        return {
          text: "Koordinaten: Adresse unverändert",
          className: "text-[11px] text-[#64748b] dark:text-[#6b7a9a]",
        };
      }
      if (meta.reason === "incomplete_address") {
        return {
          text: "Koordinaten: Unvollständig",
          className: "text-[11px] font-medium text-amber-600 dark:text-amber-400/95",
        };
      }
      if (meta.reason === "provider_unavailable") {
        return {
          text: "Koordinaten: Unvollständig",
          className: "text-[11px] font-medium text-amber-700 dark:text-amber-500/90",
        };
      }
      return {
        text: "Koordinaten: Übersprungen",
        className: "text-[11px] text-[#64748b] dark:text-[#6b7a9a]",
      };
    }
    if (meta.status === "failed") {
      return {
        text: "Koordinaten: Fehlgeschlagen",
        className: "text-[11px] font-medium text-red-600 dark:text-red-400/95",
      };
    }
  }
  if (snapshot && snapshot.lat != null && snapshot.lng != null) {
    return {
      text: "Koordinaten: Erfolgreich",
      className: "text-[11px] text-emerald-600/95 dark:text-emerald-400/85",
    };
  }
  return {
    text: "Koordinaten: Noch nicht berechnet",
    className: "text-[11px] text-[#64748b] dark:text-[#6b7a9a]",
  };
}

export function numFieldStr(v) {
  if (v == null || v === "") return "";
  return String(v);
}

export function formatCurrencyChf2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "CHF —";
  return `CHF ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Stable room count from number input (avoids `|| 0` turning "" into 0 incorrectly for parsing). */
export function parseRoomsTotal(raw) {
  if (raw === "" || raw === null || raw === undefined) return 0;
  const s = String(raw).replace(/\u00a0/g, " ").trim();
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

export function parseRoomPriceChf(raw) {
  if (raw === "" || raw === null || raw === undefined) return NaN;
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

export function sumCoLivingRoomPricesChf(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((sum, row) => {
    const p = parseRoomPriceChf(row?.price);
    return sum + (Number.isFinite(p) && p >= 0 ? p : 0);
  }, 0);
}

export function sumFirstNCoLivingRoomPricesChf(rows, n) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  const cap = Math.min(Math.max(0, Math.floor(Number(n) || 0)), rows.length);
  let sum = 0;
  for (let i = 0; i < cap; i++) {
    const p = parseRoomPriceChf(rows[i]?.price);
    sum += Number.isFinite(p) && p >= 0 ? p : 0;
  }
  return sum;
}

/** One row per room; preserves existing row state when count changes. */
export function ensureCoLivingRoomRows(n, prev) {
  const safe = Array.isArray(prev) ? prev : [];
  return Array.from({ length: n }, (_, i) => {
    if (safe[i]) {
      const r = safe[i];
      return { ...r, available_from: r.available_from ?? "" };
    }
    return {
      name: `Zimmer ${i + 1}`,
      price: "",
      floor: "",
      size_m2: "",
      status: "Frei",
      available_from: "",
    };
  });
}

export function roundCurrency(value) {
  return Math.round(Number(value || 0));
}

export function formatCurrency(value) {
  return `CHF ${roundCurrency(value).toLocaleString("de-CH")}`;
}

export function getTodayDateString() {
  return todayBusinessDate();
}

export const MODAL_COST_FIXED_SET = new Set([
  "Miete",
  "Nebenkosten",
  "Reinigung",
  "Internet",
]);

export function newModalCostRowId() {
  return `mc-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function makeDefaultModalCostRows() {
  return [
    { id: newModalCostRowId(), cost_type: "Miete", custom_type: "", amount_chf: "", frequency: "monthly" },
    { id: newModalCostRowId(), cost_type: "Nebenkosten", custom_type: "", amount_chf: "", frequency: "monthly" },
    { id: newModalCostRowId(), cost_type: "Reinigung", custom_type: "", amount_chf: "", frequency: "monthly" },
  ];
}

export function modalRowsFromApiCosts(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return makeDefaultModalCostRows();
  return rows.map((r) => {
    const ct = String(r.cost_type || "");
    const freq = String(r.frequency || "monthly").trim().toLowerCase() || "monthly";
    if (MODAL_COST_FIXED_SET.has(ct)) {
      return {
        id: r.id,
        cost_type: ct,
        custom_type: "",
        amount_chf: String(r.amount_chf ?? ""),
        frequency: freq,
      };
    }
    return {
      id: r.id,
      cost_type: "Sonstiges",
      custom_type: ct,
      amount_chf: String(r.amount_chf ?? ""),
      frequency: freq,
    };
  });
}

export function parseModalCostAmount(raw) {
  const n = Number(String(raw ?? "").replace(",", ".").trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function resolveModalCostBackendType(row) {
  if (row.cost_type === "Sonstiges") return String(row.custom_type || "").trim();
  return String(row.cost_type || "").trim();
}

export function buildValidModalCostRows(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const row of rows) {
    if (!row || !row.cost_type) continue;
    const ct = resolveModalCostBackendType(row);
    if (!ct) continue;
    const amt = parseModalCostAmount(row.amount_chf);
    if (amt == null) continue;
    const freq = String(row.frequency || "monthly").trim().toLowerCase() || "monthly";
    if (!["monthly", "yearly", "one_time"].includes(freq)) continue;
    out.push({ cost_type: ct, amount_chf: amt, frequency: freq });
  }
  return out;
}

export function runningMonthlyCostsForUnit(unit, unitCostsRows) {
  if (!isLandlordContractLeaseStarted(unit)) return 0;
  return getUnitMonthlyRunningCosts(unit, unitCostsRows);
}

export function calculateApartmentProfit(unit, unitCostsRows) {
  if (unit.current_revenue_chf == null) return null;
  const revenue = Number(unit.current_revenue_chf);
  if (!Number.isFinite(revenue)) return null;
  return revenue - runningMonthlyCostsForUnit(unit, unitCostsRows);
}

/** Visual-only badge classes for admin units tables (design tokens). */
export function adminUnitsOccupancyBadgeClass(statusKey) {
  const base = "inline-block rounded-full px-2 py-[2px] text-[9px] font-semibold ";
  if (statusKey === "belegt") {
    return `${base}border border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.1)] text-[#3ddc84]`;
  }
  if (statusKey === "frei") {
    return `${base}border border-[rgba(255,95,109,0.2)] bg-[rgba(255,95,109,0.1)] text-[#ff5f6d]`;
  }
  if (statusKey === "teilbelegt") {
    return `${base}border border-[rgba(245,166,35,0.2)] bg-[rgba(245,166,35,0.1)] text-[#f5a623]`;
  }
  if (statusKey === "reserviert") {
    return `${base}border border-[rgba(157,124,244,0.2)] bg-[rgba(157,124,244,0.1)] text-[#9d7cf4]`;
  }
  return `${base}border border-[#1c2035] bg-[#191c28] text-[#4a5070]`;
}

export function coLivingCountCircleClass(n) {
  const v = Number(n) || 0;
  const base =
    "inline-flex h-[22px] w-[22px] items-center justify-center rounded-full font-mono text-[10px] font-semibold ";
  if (v === 0) {
    return `${base}border border-[#1c2035] bg-[#191c28] text-[#4a5070]`;
  }
  return base;
}
